import React, { useMemo } from "react";
import qrcode from 'qrcode-generator';

const FINDER_SIZE = 7;

// True for modules inside one of the three 7×7 finder patterns, which are
// drawn as rounded "eyes" instead of individual dots.
const isFinderModule = (row, col, count) => {
  const nearStart = i => i < FINDER_SIZE;
  const nearEnd = i => i >= count - FINDER_SIZE;
  return (
    (nearStart(row) && nearStart(col)) ||
    (nearStart(row) && nearEnd(col)) ||
    (nearEnd(row) && nearStart(col))
  );
};

// QR code drawn as round dots with rounded finder eyes on a transparent
// background. qrcode.react only draws square modules, so the matrix comes
// from qrcode-generator and the SVG is built here.
export default function DottedQRCode({ value, size = 106, color = '#3C3C3C', title = 'QR code' }) {
  const { count, dots } = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    const moduleCount = qr.getModuleCount();
    const darkDots = [];
    for (let row = 0; row < moduleCount; row += 1) {
      for (let col = 0; col < moduleCount; col += 1) {
        if (qr.isDark(row, col) && !isFinderModule(row, col, moduleCount)) {
          darkDots.push(`${row}-${col}`);
        }
      }
    }
    return { count: moduleCount, dots: darkDots };
  }, [value]);

  const finderOrigins = [
    [0, 0],
    [count - FINDER_SIZE, 0],
    [0, count - FINDER_SIZE],
  ];

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${count} ${count}`}
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      {finderOrigins.map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect x={x + 0.5} y={y + 0.5} width={6} height={6} rx={1.9} fill="none" stroke={color} strokeWidth={1} />
          <rect x={x + 2} y={y + 2} width={3} height={3} rx={1.1} fill={color} />
        </g>
      ))}
      {dots.map(key => {
        const [row, col] = key.split('-').map(Number);
        return <circle key={key} cx={col + 0.5} cy={row + 0.5} r={0.45} fill={color} />;
      })}
    </svg>
  );
}
